import * as React from 'react';
import { Button } from '@material-ui/core';

import { RouteComponentProps } from 'react-router';
import { WidgetHeader } from 'app/components/WidgetHeader/WidgetHeader';
import {
  ISupportedRoutes,
  IVehicleTypeListModel,
  IHotspotModel,
  IRegion,
  INewHotspotModel,
} from 'app/models';
import { AddHotspotForm } from 'app/components/AddHotspotForm/AddHotspotForm';
import {
  geoCoordinatesFromAddress,
  getFirstLineAddressFromCoordinates,
} from 'app/helpers/map-helper';
import { ConfirmationModal } from 'app/components/Modals/ConfirmationModal/ConfirmationModal';

// eslint-disable-next-line

export interface IProps extends RouteComponentProps<any> {
  selectedRegion: IRegion | null;
  vehicleTypesList: IVehicleTypeListModel[];
  fetchVehicleTypesList: () => void;
  fetchHotspotDetails: (id: string) => void;
  saveHotspot: (hotspot: INewHotspotModel, file: any) => void;
  deleteHotspot: () => void;
  selectedHotspot: IHotspotModel | null;
  isEditing: boolean;
}

export interface IState {
  hotspot: INewHotspotModel;
  hasChangedName: boolean;
  confirmModal: boolean;
  nameError: string | undefined;
  vehicleTypeError: string | undefined;
  file: any;
}

export class AddHotspot extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      hotspot: {
        name: '',
        priority: 0,
        capacity: 0,
        location: {
          lat: 0,
          long: 0,
        },
        vehicleTypeId: '',
        priorityPeriods: [],
        needsProof: false,
      },
      hasChangedName: props.selectedHotspot !== null || props.match.params.id !== undefined,
      confirmModal: false,
      nameError: undefined,
      vehicleTypeError: undefined,
      file: null,
    };
  }

  async getInitialLocation() {
    const { selectedRegion } = this.props;
    if (!selectedRegion) {
      return;
    }
    const location = await geoCoordinatesFromAddress(
      `${selectedRegion.name}, ${selectedRegion.country}`,
    );
    const address = await getFirstLineAddressFromCoordinates(location);

    this.setState({
      hotspot: {
        ...this.state.hotspot,
        location: {
          ...location,
          address,
        },
        name: address,
      },
    });
  }

  async componentDidMount() {
    const {
      vehicleTypesList,
      fetchVehicleTypesList,
      selectedHotspot,
      match: { params },
      fetchHotspotDetails,
      isEditing,
    } = this.props;
    if (!vehicleTypesList.length) {
      fetchVehicleTypesList();
    }
    if (selectedHotspot) {
      this.setState({
        hotspot: {
          ...selectedHotspot,
          location: {
            ...selectedHotspot.location,
          },
        },
      });
      return;
    }
    if (params.id && !isEditing) {
      fetchHotspotDetails(params.id);
      return;
    }
    if (!selectedHotspot) {
      this.getInitialLocation();
    }
  }

  async componentDidUpdate(prevProps: IProps, prevState: IState) {
    const { selectedRegion, selectedHotspot } = this.props;
    if (
      prevProps.selectedRegion !== selectedRegion &&
      selectedRegion &&
      selectedRegion.key === 'all'
    ) {
      this.props.history.push(ISupportedRoutes.DASHBOARD);
      return;
    }
    if (prevProps.selectedRegion && selectedRegion && prevProps.selectedRegion !== selectedRegion) {
      this.props.history.push(`/${selectedRegion.key}${ISupportedRoutes.HOT_SPOTS}`);
      return;
    }

    const updateName = selectedHotspot !== null && !this.state.hasChangedName;

    const prevLocation = prevState.hotspot.location;
    const nextLocation = this.state.hotspot.location;

    if (prevLocation.lat !== nextLocation.lat || prevLocation.long !== nextLocation.long) {
      const address = await getFirstLineAddressFromCoordinates(nextLocation);
      this.setState({
        hotspot: {
          ...this.state.hotspot,
          name: updateName ? address : this.state.hotspot.name,
          location: {
            ...this.state.hotspot.location,
            address,
          },
        },
      });
    }
  }

  handleChange = (newHotspot: INewHotspotModel) => {
    const hasChangedName = newHotspot.name !== this.state.hotspot.name;
    this.setState({
      hotspot: newHotspot,
      hasChangedName: this.state.hasChangedName || hasChangedName,
      nameError: undefined,
      vehicleTypeError: undefined,
    });
  };

  handleSubmit = () => {
    let nameError: string | undefined = undefined;
    let vehicleTypeError: string | undefined = undefined;
    if (this.state.hotspot.name.trim().length === 0) {
      nameError = 'Please give a name.';
    }
    if (this.state.hotspot.vehicleTypeId.trim() === '') {
      vehicleTypeError = 'Please select vehicle type.';
    }
    if (nameError || vehicleTypeError) {
      this.setState({
        nameError,
        vehicleTypeError,
      });
    } else {
      this.props.saveHotspot(this.state.hotspot, this.state.file);
    }
  };

  handleDelete = () => {
    this.props.deleteHotspot();
    this.setState({
      confirmModal: false,
    });
  };

  renderRightPanel = (): JSX.Element => {
    const { selectedRegion, isEditing } = this.props;
    return (
      <div>
        {isEditing && (
          <Button
            onClick={() => this.setState({ confirmModal: true })}
            color="secondary"
            style={{ color: 'rgb(220, 0, 78)', marginRight: 5 }}
          >
            DELETE
          </Button>
        )}
        <Button
          variant="contained"
          size="medium"
          style={{ marginRight: 5 }}
          onClick={() =>
            this.props.history.push(
              `${selectedRegion ? '/' + selectedRegion.key : ''}${ISupportedRoutes.HOT_SPOTS}`,
            )
          }
        >
          CANCEL
        </Button>
        <Button variant="contained" size="medium" color="primary" onClick={this.handleSubmit}>
          {isEditing ? 'UPDATE' : 'SAVE'}
        </Button>
      </div>
    );
  };

  render(): JSX.Element {
    const { selectedRegion, vehicleTypesList, selectedHotspot, isEditing } = this.props;
    const { hotspot, confirmModal, nameError, vehicleTypeError } = this.state;
    return (
      <div className={'container'}>
        <div className="medium-width">
          <WidgetHeader
            headerText={
              isEditing && selectedHotspot ? 'Edit ' + selectedHotspot.name : 'Create new Hotspot'
            }
            headerSmallText={selectedRegion ? selectedRegion.name.toUpperCase() : 'All'}
            rightPanel={this.renderRightPanel()}
          />
          <AddHotspotForm
            hotspot={hotspot}
            vehicleTypesList={vehicleTypesList}
            handleChange={this.handleChange}
            nameError={nameError}
            vehicleTypeError={vehicleTypeError}
            priorityPeriods={hotspot.priorityPeriods}
            handleChangeFile={(file) => this.setState({ file })}
          />
        </div>
        {selectedHotspot && (
          <ConfirmationModal
            openModal={confirmModal}
            handleClose={() =>
              this.setState({
                confirmModal: false,
              })
            }
            handleContinue={this.handleDelete}
            modalTitle={`Delete ${hotspot.name}?`}
            modalBody={
              <span>Are you sure you want to delete it? This operation cannot be undone.</span>
            }
          />
        )}
      </div>
    );
  }
}

export default AddHotspot;
